import { useMutation } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Icon, type IconName } from '../components/Icon.js'
import { Button, Field, Input } from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { logoSrc } from '../lib/files.js'
import { useSession } from '../lib/session.js'
import { errorMessage, fieldErrors, setBearerToken, useTRPC } from '../lib/trpc.js'

export function Login() {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, refresh } = useSession()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const next = searchParams.get('next') ?? '/'

  const login = useMutation(
    trpc.auth.login.mutationOptions({
      onSuccess: async (result) => {
        // Stored only for the native build, which cannot use a cookie. On the
        // web this is redundant and harmless.
        setBearerToken(result.token)
        await refresh()
        navigate(next, { replace: true })
      },
    }),
  )

  if (user) return <Navigate to={next} replace />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    login.mutate({ email, password, deviceName: deviceName() })
  }

  const errors = fieldErrors(login.error)

  return (
    <AuthLayout title="Sign in" subtitle="Welcome back. Use your work email and password.">
      <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
        {searchParams.get('reset') === '1' && !login.error ? (
          <p
            className="flex items-start gap-2 rounded-md bg-brand-100 px-3 py-2 text-sm text-brand-800 ring-1 ring-brand-200 ring-inset"
            role="status"
          >
            <Icon name="check" className="mt-px size-4" strokeWidth={2} />
            Your password has been changed. Sign in with the new one.
          </p>
        ) : null}

        <Field label="Email" htmlFor="email" error={errors['email']} required>
          <Input
            id="email"
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <div className="flex flex-col gap-2">
          <Field label="Password" htmlFor="password" error={errors['password']} required>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                required
                className="pr-10"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 grid w-10 place-items-center rounded-r-md text-ink-400 hover:text-ink-700 focus-visible:text-ink-700 focus-visible:outline-none"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((shown) => !shown)}
              >
                <Icon name={showPassword ? 'eyeOff' : 'eye'} className="size-4" strokeWidth={2} />
              </button>
            </div>
          </Field>
          <Link
            to="/forgot-password"
            className="self-end text-xs font-medium text-brand-700 hover:text-brand-800"
          >
            Forgotten your password?
          </Link>
        </div>

        {login.error ? (
          <p
            className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200 ring-inset"
            role="alert"
          >
            <Icon name="alert" className="mt-px size-4" strokeWidth={2} />
            {errorMessage(login.error)}
          </p>
        ) : null}

        <Button type="submit" variant="primary" size="lg" block loading={login.isPending}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  )
}

/**
 * Names the session after the device.
 *
 * Coarse on purpose: enough for someone to recognise the phone they lost in
 * a list of active sessions, without parsing a user-agent string into a
 * fiction about browser versions.
 */
function deviceName(): string {
  const ua = navigator.userAgent
  if (/iPhone|iPad/.test(ua)) return 'iPhone or iPad'
  if (/Android/.test(ua)) return 'Android device'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows PC'
  return 'Browser'
}

const LOGO_PATH = '/public/brand/logo'

const HIGHLIGHTS: { icon: IconName; title: string; body: string }[] = [
  {
    icon: 'schedule',
    title: 'One board for every crew',
    body: 'Book, move and confirm visits across branches.',
  },
  {
    icon: 'spray',
    title: 'Reports from the field',
    body: 'Findings and photos captured on site, PDF ready when the job is.',
  },
  {
    icon: 'invoices',
    title: 'Quotes and invoices',
    body: 'Raised, sent and tracked against the job they belong to.',
  },
]

/**
 * Sign in, forgotten password, reset and invitation all share this frame. On a
 * wide screen the brand panel sits beside the form; on a phone it collapses to
 * the mark above the form, because a technician signing in at the van does not
 * need to scroll past marketing.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: ReactNode
}) {
  return (
    <div className="min-h-dvh bg-white lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <aside className="relative hidden overflow-hidden bg-ink-900 text-white lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-16">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-48 -left-40 size-[40rem] rounded-full bg-brand-600/20 blur-3xl" />
          <div className="absolute -right-32 -bottom-56 size-[32rem] rounded-full bg-brand-900/60 blur-3xl" />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(rgb(255 255 255 / 0.07) 1px, transparent 1px)',
              backgroundSize: '22px 22px',
              maskImage: 'linear-gradient(to bottom, black, transparent 75%)',
            }}
          />
        </div>

        <img
          src="/brand/ecopest-horizontal-colour-on-dark.svg"
          alt="EcoPest"
          className="relative h-10 w-auto self-start"
        />

        <div className="relative max-w-md">
          <p className="text-xs font-semibold tracking-[0.18em] text-brand-400 uppercase">
            Field service, end to end
          </p>
          <p className="mt-4 font-display text-3xl leading-tight font-extrabold text-balance xl:text-4xl">
            Every job, from the first call to the final invoice.
          </p>
          <ul className="mt-10 flex flex-col gap-6">
            {HIGHLIGHTS.map((item) => (
              <li key={item.title} className="flex gap-4">
                <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-white/5 text-brand-400 ring-1 ring-white/10">
                  <Icon name={item.icon} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">{item.title}</span>
                  <span className="mt-0.5 block text-sm text-ink-400">{item.body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-ink-500">
          © {new Date().getFullYear()} EcoPest Group. For staff use only.
        </p>
      </aside>

      <main className="flex min-h-dvh flex-col px-6 py-10 sm:px-10">
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
          <div className="mb-10">
            <BrandMark />
          </div>

          <h1 className="font-display text-2xl font-extrabold text-ink-900">{title}</h1>
          {subtitle ? <p className="mt-2 text-sm text-ink-500">{subtitle}</p> : null}

          <div className="mt-8">{children}</div>
        </div>

        <p className="mx-auto mt-10 w-full max-w-sm text-xs text-ink-400">
          Trouble signing in? Ask the office to check your account.
        </p>
      </main>
    </div>
  )
}

/**
 * The logo uploaded in Settings, or the brand pack's own when there is none.
 * `/public/brand/logo` answers 404 until one is uploaded, so the bundled logo
 * is the error path; the reserved height keeps the heading from jumping while
 * the image loads.
 */
function BrandMark() {
  const [state, setState] = useState<'pending' | 'loaded' | 'failed'>('pending')
  const src = logoSrc(LOGO_PATH)

  if (state === 'failed' || !src) {
    return (
      <div className="flex h-14 items-center">
        <img src="/brand/ecopest-horizontal-colour-on-light.svg" alt="EcoPest" className="h-12 w-auto" />
      </div>
    )
  }

  return (
    <div className="flex h-14 items-center">
      <img
        src={src}
        alt="EcoPest"
        className={cn('max-h-14 w-auto max-w-56 object-contain', state !== 'loaded' && 'invisible')}
        onLoad={() => setState('loaded')}
        onError={() => setState('failed')}
      />
    </div>
  )
}
